import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom Hook for Authentication Modal
 * 
 * Provides a simple way to require authentication for protected actions.
 * 
 * Usage:
 * ```typescript
 * const { showAuthModal, setShowAuthModal, requireAuth } = useAuthModal();
 * 
 * // Check auth and execute callback
 * const handleProtectedAction = () => {
 *   if (requireAuth(() => {
 *     // This code only runs if user is signed in
 *     performAction();
 *   })) {
 *     // User is authenticated
 *   } else {
 *     // Modal was shown
 *   }
 * };
 * 
 * // Or just check auth without callback
 * if (!requireAuth()) {
 *   return; // Modal was shown
 * }
 * // Continue with protected action
 * ```
 */
export const useAuthModal = () => {
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isSignedIn = !!user;

  /**
   * Require authentication for an action
   * Shows auth modal if user is not signed in
   * 
   * @param callback - Optional callback to execute if user is authenticated
   * @returns true if user is authenticated, false if modal was shown
   */
  const requireAuth = useCallback((callback?: () => void): boolean => {
    if (!isSignedIn) {
      setShowAuthModal(true);
      return false;
    }
    
    callback?.();
    return true;
  }, [isSignedIn]);

  return {
    showAuthModal,
    setShowAuthModal,
    requireAuth,
    isSignedIn,
  };
};

